// src/pages/admin/AdminListingDetail.tsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  X,
  MapPin,
  Phone,
  User,
  Ruler,
  Calendar,
  IndianRupee,
  Image as ImageIcon,
  AlertCircle,
  RefreshCw,
  Tag,
  FileText,
  Building2,
  CheckCircle2,
  XCircle,
  Clock3,
  Navigation,
  StickyNote,
  Layers,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Copy,
  Check,
  Percent,
  ClipboardCheck,
  Loader2,
  Send,
  ChevronDown,
} from 'lucide-react';
import {
  getAdminListingById,
  updateListingApproval,
  ListingApprovalStatus,
} from '../../services/Adminlistings.api';
import { AdminListingDetail as AdminListingDetailModel } from '../../types/adminlistingsmodel';

type ListingStatusMeta = { label: string; badgeClasses: string; iconClasses: string; icon: React.ElementType };

interface AdminListingDetailProps {
  listingId: string | null;
  open: boolean;
  onClose: () => void;
  /** Called after the listing's approval status is successfully updated — use it to refresh a parent list. */
  onUpdated?: (listingId: string, status: ListingApprovalStatus) => void;
}

/* ---------- helpers ---------- */

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '—';
  return `₹${value.toLocaleString('en-IN')}`;
};

const googleMapsUrl = (lat: number | string, lng: number | string) =>
  `https://www.google.com/maps?q=${lat},${lng}`;

const statusConfig: Record<string, ListingStatusMeta> = {
  approved: {
    label: 'Approved',
    badgeClasses: 'bg-white/15 text-white ring-1 ring-inset ring-white/30',
    iconClasses: 'text-emerald-300',
    icon: CheckCircle2,
  },
  pending: {
    label: 'Pending review',
    badgeClasses: 'bg-white/15 text-white ring-1 ring-inset ring-white/30',
    iconClasses: 'text-amber-200',
    icon: Clock3,
  },
  rejected: {
    label: 'Rejected',
    badgeClasses: 'bg-white/15 text-white ring-1 ring-inset ring-white/30',
    iconClasses: 'text-rose-200',
    icon: XCircle,
  },
};

/** Dropdown options for the review form — value is exactly what gets submitted to the API. */
const reviewOptions: { value: ListingApprovalStatus; label: string }[] = [
  { value: 'approved', label: 'Approve' },
  { value: 'rejected', label: 'Reject' },
  { value: 'changes_requested', label: 'Changes requested' },
];

/* ---------- small building blocks ---------- */

const SectionHeading: React.FC<{ icon: React.ElementType; title: string; action?: React.ReactNode }> = ({
  icon: Icon,
  title,
  action,
}) => (
  <div className="flex items-center justify-between gap-3 mb-4">
    <div className="flex items-center gap-2.5">
      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 shadow-sm shadow-orange-200">
        <Icon className="w-4 h-4 text-white" />
      </div>
      <h3 className="text-sm font-bold text-slate-800 tracking-wide">{title}</h3>
    </div>
    {action}
  </div>
);

const Stat: React.FC<{ label: string; value: React.ReactNode; icon?: React.ElementType }> = ({
  label,
  value,
  icon: Icon,
}) => (
  <div className="rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-3">
    <span className="flex items-center gap-1 text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </span>
    <span className="block text-sm text-slate-800 font-semibold break-words leading-snug">
      {value ?? '—'}
    </span>
  </div>
);

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div
    className={`bg-white rounded-2xl border border-slate-200/80 p-4 sm:p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-16px_rgba(15,23,42,0.12)] ${className}`}
  >
    {children}
  </div>
);

/* ---------- skeleton ---------- */

const DetailSkeleton: React.FC = () => (
  <div className="p-5 space-y-5 animate-pulse">
    <div className="h-40 bg-slate-100 rounded-2xl" />
    <div className="h-40 bg-slate-100 rounded-2xl" />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="h-32 bg-slate-100 rounded-2xl" />
      <div className="h-32 bg-slate-100 rounded-2xl" />
    </div>
    <div className="h-24 bg-slate-100 rounded-2xl" />
  </div>
);

/* ---------- error state ---------- */

const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-rose-50 mb-4">
      <AlertCircle className="w-6 h-6 text-rose-500" />
    </div>
    <p className="text-sm font-semibold text-slate-800 mb-1">Couldn't load this listing</p>
    <p className="text-sm text-slate-500 mb-5 max-w-xs">{message}</p>
    <button
      onClick={onRetry}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 transition-colors shadow-sm shadow-orange-200"
    >
      <RefreshCw className="w-4 h-4" />
      Try again
    </button>
  </div>
);

/* ---------- photo lightbox ---------- */

const PhotoLightbox: React.FC<{
  photos: string[];
  index: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}> = ({ photos, index, onClose, onNavigate }) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNavigate((index + 1) % photos.length);
      if (e.key === 'ArrowLeft') onNavigate((index - 1 + photos.length) % photos.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [index, photos.length, onClose, onNavigate]);

  return (
    <div
      className="fixed inset-0 z-[1500] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
      >
        <X className="w-6 h-6" />
      </button>

      {photos.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate((index - 1 + photos.length) % photos.length);
          }}
          className="absolute left-4 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
        >
          <ChevronLeft className="w-7 h-7" />
        </button>
      )}

      <img
        src={photos[index]}
        alt={`Listing photo ${index + 1}`}
        className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />

      {photos.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate((index + 1) % photos.length);
          }}
          className="absolute right-4 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
        >
          <ChevronRight className="w-7 h-7" />
        </button>
      )}

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/70 text-xs font-medium">
        {index + 1} / {photos.length}
      </div>
    </div>
  );
};

/* ---------- main component ---------- */

const AdminListingDetail: React.FC<AdminListingDetailProps> = ({
  listingId,
  open,
  onClose,
  onUpdated,
}) => {
  const [data, setData] = useState<AdminListingDetailModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  // review form state
  const [reviewStatus, setReviewStatus] = useState<ListingApprovalStatus | ''>('');
  const [reviewNote, setReviewNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!listingId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getAdminListingById(listingId);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch listing details.');
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    if (open && listingId) {
      fetchDetail();
    }
    if (!open) {
      // reset when closed so next open starts fresh
      setData(null);
      setError(null);
      setLightboxIndex(null);
      setCopied(false);
      setReviewStatus('');
      setReviewNote('');
      setSubmitError(null);
      setSubmitSuccess(false);
    }
  }, [open, listingId, fetchDetail]);

  // lock background scroll while the modal is open
  useEffect(() => {
    if (open) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
  }, [open]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && lightboxIndex === null) onClose();
    };
    if (open) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, onClose, lightboxIndex]);

  const handleCopyCoordinates = async (lat: number | string, lng: number | string) => {
    try {
      await navigator.clipboard.writeText(`${lat}, ${lng}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard not available — silently ignore
    }
  };

  const handleSubmitReview = async () => {
    if (!listingId || !reviewStatus) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    try {
      await updateListingApproval(listingId, {
        status: reviewStatus,
        adminNote: reviewNote.trim() || undefined,
      });
      setSubmitSuccess(true);
      onUpdated?.(listingId, reviewStatus);
      // refresh the listing so the header badge / admin note reflect the change
      await fetchDetail();
      setTimeout(() => setSubmitSuccess(false), 2500);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to update listing status.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const listing = data?.listing;
  const status = listing ? statusConfig[listing.approvalStatus] ?? statusConfig.pending : null;
  const StatusIcon = status?.icon ?? Clock3;

  const charges = listing?.additionalCharges;
  const hasExtraCharges =
    charges &&
    (charges.wallPaintingCharge > 0 ||
      charges.installationCharge > 0 ||
      charges.removalCharge > 0 ||
      charges.otherCharge > 0);

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center p-2 sm:p-6"
      role="dialog"
      aria-modal="true"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* modal */}
      <div className="relative z-[1401] w-[90vw] max-w-[90vw] h-[92vh] max-h-[92vh] bg-slate-50 rounded-2xl shadow-2xl ring-1 ring-black/5 flex flex-col overflow-hidden animate-[fadeIn_0.15s_ease-out]">
        {/* header */}
        <div className="relative shrink-0 bg-gradient-to-r from-orange-400 via-orange-300 to-amber-500 px-5 sm:px-7 py-5 overflow-hidden">
          {/* decorative texture */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 20%, white 0%, transparent 35%), radial-gradient(circle at 85% 60%, white 0%, transparent 40%)',
            }}
          />
          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-white/70">
                  Listing detail
                </span>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white truncate max-w-[60vw] mt-0.5">
                {listing?.title || (loading ? 'Loading listing…' : 'Listing details')}
              </h2>
              <div className="flex items-center gap-2 flex-wrap mt-2">
                {status && (
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${status.badgeClasses}`}
                  >
                    <StatusIcon className={`w-3.5 h-3.5 ${status.iconClasses}`} />
                    {status.label}
                  </span>
                )}
                {listing && (
                  <span className="text-xs text-white/75 font-mono">
                   S.No {listing.sno}
                  </span>
                )}
              </div>
            </div>

            <button
              onClick={onClose}
              className="shrink-0 flex items-center justify-center w-9 h-9 rounded-full text-white/80 hover:text-white hover:bg-white/15 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* scrollable body */}
        <div
          className="overflow-y-auto grow px-5 sm:px-7 py-5
          [&::-webkit-scrollbar]:w-2
          [&::-webkit-scrollbar-track]:bg-transparent
          [&::-webkit-scrollbar-thumb]:bg-slate-300
          [&::-webkit-scrollbar-thumb]:rounded-full
          hover:[&::-webkit-scrollbar-thumb]:bg-slate-400"
        >
          {loading && <DetailSkeleton />}

          {!loading && error && <ErrorState message={error} onRetry={fetchDetail} />}

          {!loading && !error && data && listing && (
            <div className="space-y-5 max-w-6xl mx-auto">
              {/* photos */}
              {listing.photos && listing.photos.length > 0 ? (
                <Card className="p-2 sm:p-2">
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                    {listing.photos.map((photo, idx) => (
                      <button
                        key={idx}
                        onClick={() => setLightboxIndex(idx)}
                        className="relative aspect-square rounded-xl overflow-hidden group ring-1 ring-slate-200"
                      >
                        <img
                          src={photo}
                          alt={`Listing ${idx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      </button>
                    ))}
                  </div>
                </Card>
              ) : (
                <Card className="flex items-center gap-3 text-slate-400">
                  <ImageIcon className="w-5 h-5" />
                  <span className="text-sm">No photos uploaded for this listing</span>
                </Card>
              )}

              {/* key details — dense stat grid, no dead space */}
              <Card>
                <SectionHeading icon={Layers} title="Listing details" />
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  <Stat label="Media type" value={listing.mediaType} />
                  <Stat
                    label="Dimensions"
                    icon={Ruler}
                    value={`${listing.width} × ${listing.height} ft`}
                  />
                  <Stat label="Location type" value={data.locationTypeName} />
                  <Stat label="Traffic level" value={data.locationTypetrafficLevel} />
                  <Stat label="Available from" icon={Calendar} value={formatDate(listing.availableFrom)} />
                  <Stat label="Available to" icon={Calendar} value={formatDate(listing.availableTo)} />
                  <Stat
                    label="Expected rent"
                    icon={IndianRupee}
                    value={<span className="text-orange-600">{formatCurrency(listing.expectedRent)}</span>}
                  />
                  <Stat
                    label="Commission"
                    icon={Percent}
                    value={`${data.locationTypeCommissionPercentage}%`}
                  />
                </div>
              </Card>

              {/* location */}
              <Card>
                <SectionHeading
                  icon={MapPin}
                  title="Location"
                  action={
                    <a
                      href={googleMapsUrl(listing.latitude, listing.longitude)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-600 text-white text-xs font-semibold hover:bg-orange-700 transition-colors shadow-sm shadow-orange-200"
                    >
                      <Navigation className="w-3.5 h-3.5" />
                      Open in Google Maps
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  }
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Stat
                    label="Address"
                    value={`${listing.area}, ${listing.landmark ? listing.landmark + ', ' : ''}${listing.city}`}
                  />
                  <Stat label="State / Country" value={`${listing.state}, ${listing.country}`} />
                  <Stat label="Pin code" value={listing.pinCode} />
                  <div className="rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="flex items-center gap-1 text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
                        <Navigation className="w-3 h-3" />
                        Coordinates
                      </span>
                      <a
                        href={googleMapsUrl(listing.latitude, listing.longitude)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-orange-600 font-mono font-semibold hover:text-orange-700 hover:underline truncate"
                      >
                        {listing.latitude}, {listing.longitude}
                      </a>
                    </div>
                    <button
                      onClick={() => handleCopyCoordinates(listing.latitude, listing.longitude)}
                      className="shrink-0 flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                      aria-label="Copy coordinates"
                      title="Copy coordinates"
                    >
                      {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </Card>

              {/* additional charges */}
              {hasExtraCharges && charges && (
                <Card>
                  <SectionHeading icon={Tag} title="Additional charges" />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {charges.wallPaintingCharge > 0 && (
                      <Stat label="Wall painting" value={formatCurrency(charges.wallPaintingCharge)} />
                    )}
                    {charges.installationCharge > 0 && (
                      <Stat label="Installation" value={formatCurrency(charges.installationCharge)} />
                    )}
                    {charges.removalCharge > 0 && (
                      <Stat label="Removal" value={formatCurrency(charges.removalCharge)} />
                    )}
                    {charges.otherCharge > 0 && (
                      <Stat
                        label={charges.otherChargeDescription || 'Other'}
                        value={formatCurrency(charges.otherCharge)}
                      />
                    )}
                  </div>
                </Card>
              )}

              {/* description + provider side by side to fill width evenly */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {listing.description && (
                  <Card className="lg:col-span-3">
                    <SectionHeading icon={FileText} title="Description" />
                    <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                      {listing.description}
                    </p>
                  </Card>
                )}

                <Card className={listing.description ? 'lg:col-span-2' : 'lg:col-span-5'}>
                  <SectionHeading icon={Building2} title="Provider" />
                  <div className="grid grid-cols-1 gap-3">
                    <Stat label="Name" icon={User} value={data.providerName} />
                    <Stat label="Mobile" icon={Phone} value={data.providerMobile} />
                  </div>
                </Card>
              </div>

              {/* location type description */}
              {data.locationTypeDescription && (
                <Card>
                  <SectionHeading icon={Layers} title="Location type notes" />
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {data.locationTypeDescription}
                  </p>
                </Card>
              )}

              {/* admin note (last saved) */}
              {listing.adminNote && (
                <Card className="bg-amber-50/60 border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <StickyNote className="w-4 h-4 text-amber-600" />
                    <h3 className="text-sm font-semibold text-amber-800">Admin note</h3>
                  </div>
                  <p className="text-sm text-amber-800/90 leading-relaxed">
                    {listing.adminNote}
                  </p>
                </Card>
              )}

              {/* metadata */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs text-slate-400 px-1 pb-2">
                <div>
                  <span className="block uppercase tracking-wide mb-0.5">Created</span>
                  <span className="text-slate-600 font-medium">
                    {formatDate(listing.cdate)} {listing.ctime}
                  </span>
                </div>
                <div>
                  <span className="block uppercase tracking-wide mb-0.5">Updated</span>
                  <span className="text-slate-600 font-medium">{formatDate(listing.edate)}</span>
                </div>
              </div>

              {/* review form */}
              <Card className="border-orange-200/80">
                <SectionHeading icon={ClipboardCheck} title="Review this listing" />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-1">
                    <label
                      htmlFor="admin-review-decision"
                      className="block text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5"
                    >
                      Decision
                    </label>
                    <div className="relative">
                      <select
                        id="admin-review-decision"
                        value={reviewStatus}
                        onChange={(e) => setReviewStatus(e.target.value as ListingApprovalStatus)}
                        className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 pr-9 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300 transition-colors cursor-pointer"
                      >
                        <option value="" disabled>
                          Select a decision…
                        </option>
                        {reviewOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    </div>
                  </div>

                  <div className="sm:col-span-2 flex flex-col">
                    <label
                      htmlFor="admin-review-note"
                      className="block text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5"
                    >
                      Note for the provider (optional)
                    </label>
                    <textarea
                      id="admin-review-note"
                      value={reviewNote}
                      onChange={(e) => setReviewNote(e.target.value)}
                      placeholder="e.g. Photos are blurry, please re-upload before approval."
                      rows={4}
                      className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300 transition-colors"
                    />

                    <div className="flex items-center justify-between gap-3 mt-3">
                      <div className="text-xs">
                        {submitError && <span className="text-rose-600 font-medium">{submitError}</span>}
                        {submitSuccess && (
                          <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                            <Check className="w-3.5 h-3.5" />
                            Listing status updated
                          </span>
                        )}
                      </div>
                      <button
                        onClick={handleSubmitReview}
                        disabled={submitting || !reviewStatus}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-orange-500 text-white text-sm font-semibold hover:from-orange-700 hover:to-orange-600 transition-colors shadow-sm shadow-orange-200 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                      >
                        {submitting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        {submitting ? 'Submitting…' : 'Submit review'}
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      {lightboxIndex !== null && listing?.photos && (
        <PhotoLightbox
          photos={listing.photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </div>
  );
};

export default AdminListingDetail;